import { helpersUtils } from '@luigiclaudio/ga-baseline-ui/helpers';
import { navigate } from 'gatsby';
import { parseToHsl } from 'polished';
import { getAnalyticsTool, getAnalyticsUser, trackEvent } from './analytics';
import { GET_COLORS } from '../services/actionTypes';
import colorLibraryActivation from '../../shared/colorLibraryActivation.cjs';
import { rememberLibrarySignupContext } from './librarySignupContext';

const performColorClick = async ({
    currentColor,
    currentId,
    setAllColorsUserFromClient,
    allColorsUserFromClient,
    client,
    deleteColor,
    createColor,
    user,
    pathname,
}) => {
    const analyticsContext = {
        ...getAnalyticsUser(user),
        tool: getAnalyticsTool(pathname),
        feature: 'color_library',
    };
    const intentAction = colorLibraryActivation.saveIntentAction(user);
    if (!currentId) trackEvent('add_to_library_click', analyticsContext);
    if (intentAction === 'pending') {
        rememberLibrarySignupContext('save_intent', { returnTo: pathname, color: currentColor });
        navigate('/signUp/', {
            state: colorLibraryActivation.signupState('save_intent'),
        });
        return;
    }

    if (intentAction === 'save') {
        const hsl = parseToHsl(currentColor);

        // Await server confirmation before updating the visible saved state.
        try {
            if (currentId && deleteColor)
                await deleteColor({ variables: { color: currentColor, ...hsl } });
            if (!currentId) {
                trackEvent('save_attempt', analyticsContext);
                await createColor({ variables: { color: currentColor, ...hsl } });
            }
        } catch (error) {
            trackEvent('save_failed', { ...analyticsContext, reason: 'request_failed' });
            throw error;
        }
        client.cache.updateQuery({ query: GET_COLORS }, (currentData) => {
            const currentItem = {
                __typename: 'Color',
                color: currentColor,
                ...hsl,
                id: helpersUtils.generateUniqueId(),
            };

            if (!currentId) {
                const addToMutationData = [currentItem];
                if (!currentData) {
                    addToMutationData.push(...(allColorsUserFromClient?.allColors?.data || []));
                } else {
                    addToMutationData.push(...currentData.allColors.data);
                }
                setAllColorsUserFromClient({
                    allColors: {
                        __typename: 'ColorPage',
                        data: addToMutationData,
                    },
                });

                return {
                    allColors: {
                        __typename: 'ColorPage',
                        data: addToMutationData,
                    },
                };
            }

            if (currentId && deleteColor) {
                let removeFromMutationData;

                if (!currentData) {
                    removeFromMutationData = allColorsUserFromClient.allColors.data.filter(
                        (item) => item.color !== currentColor,
                    );
                } else {
                    removeFromMutationData = currentData.allColors.data.filter(
                        (item) => item.color !== currentColor,
                    );
                }
                setAllColorsUserFromClient({
                    allColors: {
                        __typename: 'ColorPage',
                        data: removeFromMutationData,
                    },
                });

                return {
                    allColors: {
                        __typename: 'ColorPage',
                        data: removeFromMutationData,
                    },
                };
            }

            return null;
        });
    }

    if (intentAction === 'authenticate') {
        rememberLibrarySignupContext('save_intent', { returnTo: pathname, color: currentColor });
        navigate('/signUp/', {
            state: {
                pathname,
                signupSource: 'save_intent',
                experiment: colorLibraryActivation.EXPERIMENT,
            },
        });
    }
};

const pending = new Map();
const handleOnColorClick = (options) => {
    const key = JSON.stringify([options.user?.netlifyId, options.currentColor, options.currentId]);
    if (pending.has(key)) return pending.get(key);
    const request = performColorClick(options).finally(() => pending.delete(key));
    pending.set(key, request);
    return request;
};
export default handleOnColorClick;
